export const DISCOUNT_TYPE = {
  PERCENTAGE: "Percentage",
  FIXED_AMOUNT: "Fixed Amount",
};

export const PROMOTION_TARGET = {
  SUBTOTAL: "Subtotal",
  DELIVERY_FEE: "Delivery Fee",
  SERVICE_FEE: "Service fee",
};

export const PROMO_STATUS = {
  ACTIVE: "Active",
  SCHEDULED: "Scheduled",
  EXPIRED: "Expired",
  DRAFT: "Draft",
  DISABLED: "Disabled",
};
// ---------- Form helpers (shared by discount UI components) ----------

export const ORDER_TYPE_OPTIONS = [
  { value: 1, label: "Delivery" },
  { value: 2, label: "Pickup" },
  { value: 3, label: "Eat-in" },
  { value: 4, label: "Curbside" },
  { value: 5, label: "Drive-thru" },
];

export const USER_ORDER_COUNT_CONDITIONS = [
  { value: ">", label: ">" },
  { value: "=", label: "=" },
  { value: "<", label: "<" },
  { value: ">=", label: ">=" },
  { value: "<=", label: "<=" },
  { value: "<>", label: "<>" },
];

export const getMissingRequiredFields = ({
  name,
  start,
  end,
  selectedStores,
}) => {
  const missingFields = [];

  if (!name) {
    missingFields.push("Name");
  }
  if (!start) {
    missingFields.push("Start date");
  }
  if (!end) {
    missingFields.push("End date");
  }
  if (!selectedStores || selectedStores.length === 0) {
    missingFields.push("Stores (businesses)");
  }

  return missingFields;
};

export const buildApiDefaults = (initialData) => ({
  enabled: !initialData, // new promotions enabled by default
  auto: true,
  public: true,
  condition_type: 1,
  type: 1,
  target: 1,
  rate_type: 1,
  stackable: false,
  rate: 0,
});

const normalizeConditionItems = (items) => {
  if (items === null || items === undefined) {
    return items;
  }

  if (!Array.isArray(items)) {
    return items;
  }

  if (items.length === 0) {
    return [];
  }

  if (typeof items[0] === "object" && items[0] !== null) {
    return items;
  }

  return items.map((id) => ({
    id,
    include: true,
    is_condition: true,
  }));
};

const normalizeProductsToApiValue = (products) => {
  if (products === null || products === undefined) {
    return null;
  }

  if (typeof products === "string") {
    return products;
  }

  const normalized = normalizeConditionItems(products);

  if (!Array.isArray(normalized) || normalized.length === 0) {
    return null;
  }

  try {
    const cleaned = normalized
      .map((item) => {
        if (!item || typeof item !== "object") {
          return null;
        }

        const id = Number(item.id);
        if (!Number.isFinite(id)) {
          return null;
        }

        const include = typeof item.include === "boolean" ? item.include : true;

        return {
          id,
          include,
          is_condition: true,
        };
      })
      .filter(Boolean);

    if (cleaned.length === 0) {
      return null;
    }

    return JSON.stringify(cleaned);
  } catch {
    return null;
  }
};

const normalizeCategoriesToApiValue = (categories) => {
  if (categories === null || categories === undefined) {
    return null;
  }

  if (typeof categories === "string") {
    return categories;
  }

  const normalized = normalizeConditionItems(categories);

  if (!Array.isArray(normalized) || normalized.length === 0) {
    return null;
  }

  try {
    // Ensure we don't send business_id per category (API expects only condition items).
    const cleaned = normalized
      .map((item) => {
        if (!item || typeof item !== "object") {
          return null;
        }

        const id = Number(item.id);
        if (!Number.isFinite(id)) {
          return null;
        }

        const include = typeof item.include === "boolean" ? item.include : true;

        return {
          id,
          include,
          is_condition: true,
        };
      })
      .filter(Boolean);

    if (cleaned.length === 0) {
      return null;
    }

    return JSON.stringify(cleaned);
  } catch {
    return null;
  }
};

const normalizeScheduleToApiValue = (schedule) => {
  if (schedule === null || schedule === undefined) {
    return null;
  }

  // If it's already a string, assume it's already JSON.
  if (typeof schedule === "string") {
    return schedule;
  }

  try {
    return JSON.stringify(schedule);
  } catch {
    return null;
  }
};

const normalizeNullableNumberToApiValue = (value) => {
  if (value === null) {
    return null;
  }

  if (value === undefined || value === "") {
    return undefined;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return undefined;
  }

  return numericValue;
};

export const buildApiPayload = ({
  initialData,
  name,
  description,
  coupon,
  promotionType,
  imageUrl,
  startDateValue,
  endDateValue,
  rate_type,
  rate,
  target,
  stackable,
  auto,
  selectedStores,
  selectedProducts,
  selectedCategories,
  limit,
  limit_per_user,
  max_discount,
  minimum,
  user_order_count,
  user_order_count_condition,
  valid_from_after_user_last_order_minutes,
  valid_until_after_user_last_order_minutes,
  include_options,
  order_types_allowed,
  paymethods,
  delivery_zones,
  schedule,
}) => {
  const apiDefaults = buildApiDefaults(initialData);
  const normalizedType =
    promotionType === undefined || promotionType === null
      ? apiDefaults.type
      : Number(promotionType) === 2
        ? 2
        : 1;

  return {
    ...apiDefaults,
    auto: typeof auto === "boolean" ? auto : apiDefaults.auto,
    type: normalizedType,
    name,
    description,
    coupon: normalizedType === 2 ? coupon : undefined,
    image: imageUrl,
    start: `${startDateValue} 00:00:00`,
    end: `${endDateValue} 23:59:59`,
    rate_type,
    rate: Number(rate) || 0,
    target,
    stackable,
    businesses: selectedStores,
    products: normalizeProductsToApiValue(selectedProducts),
    categories: normalizeCategoriesToApiValue(selectedCategories),
    limit: normalizeNullableNumberToApiValue(limit),
    limit_per_user: normalizeNullableNumberToApiValue(limit_per_user),
    max_discount: normalizeNullableNumberToApiValue(max_discount),
    minimum: normalizeNullableNumberToApiValue(minimum),
    user_order_count: normalizeNullableNumberToApiValue(user_order_count),
    user_order_count_condition,
    valid_from_after_user_last_order_minutes: normalizeNullableNumberToApiValue(
      valid_from_after_user_last_order_minutes,
    ),
    valid_until_after_user_last_order_minutes:
      normalizeNullableNumberToApiValue(
        valid_until_after_user_last_order_minutes,
      ),
    include_options: Boolean(include_options),
    order_types_allowed,
    paymethods,
    delivery_zones,
    schedule: normalizeScheduleToApiValue(schedule),
  };
};

export const sanitizeApiPayload = (payload) => {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => {
      // Never send undefined, empty strings, or empty arrays
      if (value === undefined || value === "") {
        return false;
      }

      if (Array.isArray(value) && value.length === 0) {
        return false;
      }

      return true;
    }),
  );
};
